import { map, pipe, toArray } from '@fxts/core';
import { getSearchInputPlaceholder } from '@services/MarppleShop/features/Search/util';
import {
  ArrowLeftIcon,
  CartFillIcon,
  CloseIcon,
  HeartFillIcon,
  MarppleShopLogo,
  SearchIcon,
  UserFillIcon,
} from '@services/MarppleShop/renderApp/components/atoms/Icon';
import { InputSearch } from '@services/MarppleShop/renderApp/components/atoms/InputSearch/InputSearch.ts';
import { Modal } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { HeaderAbstract } from '@services/MarppleShop/renderApp/components/cells/Header/Base/HeaderAbstract.ts';
import {
  CreatorLogoSearch,
  CreatorSearchStatusChange,
} from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorLogoSearch.ts';
import { CreatorShopSubHeader } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorShopSubHeader.ts';
import { CreatorShopSubHeaderCategory } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorShopSubHeaderCategory.ts';
import { Menu } from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/header';
import {
  HeaderCreatorShopData,
  HeaderCreatorShopOptions,
  HeaderCreatorShopState,
} from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/headerCreatorShop';
import {
  makeCartNavigate,
  makeEmptyScreenNavigate,
  makeHomeScreenNavigate,
} from '@services/MarppleShop/shared/app/navigate.ts';
import { Locals } from '@services/MarppleShop/shared/type/locals.ts';
import { RequiredByKey } from '@services/MarppleShop/shared/type/types.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { classes, htmlIf, isAllCrewById } from '@services/MarppleShop/shared/util';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { listenDocumentEvent } from '@services/MarppleShop/shared/util/event.ts';
import { makeMainUrl, makePlusUrl } from '@services/MarppleShop/shared/util/url.ts';
import { html } from 'rune-ts';
import {
  SearchModuleCreatorShop,
  SearchModuleCreatorShopCloseEvent,
} from '../../SearchModule/SearchModuleCreatorShop.ts';
import klass from './HeaderCreatorShop.module.scss';
import { CreatorLogo, CreatorLogoProps } from './views/CreatorLogo.ts';

export class HeaderCreatorShop extends HeaderAbstract<HeaderCreatorShopData, HeaderCreatorShopState> {
  readonly INPUT_SEARCH_KLASS: Readonly<string> = 'header_input_search';
  readonly #input_search: InputSearch;
  readonly #logo: CreatorLogo;
  readonly #search_input_logo: CreatorLogoSearch;
  readonly #search_module_logo: CreatorLogoSearch;
  readonly #search_module?: SearchModuleCreatorShop;
  readonly #sub_header?: CreatorShopSubHeader;
  readonly #category_sub_header?: CreatorShopSubHeaderCategory;

  constructor(data: HeaderCreatorShopData, option: HeaderCreatorShopOptions) {
    super(
      data,
      {
        ...option,
        has_back_btn: option.has_back_btn ?? false,
        has_close_btn: option.has_close_btn ?? false,
        transparent: option.transparent ?? false,
        is_fixed: option.is_fixed ?? true,
        /**
         * @desc 크리에이터 샵 헤더는 기본적으로 헤드룸을 사용하지 않음
         */
        headroom: option.headroom ? option.headroom : { use: false },
      },
      {
        back_arrow_btn: klass.back_arrow_btn,
        center_part: klass.center_part,
        header: klass.header,
        instant_bg_change: klass.instant_bg_change,
        menu: klass.menu,
        transparent: klass.transparent,
      },
    );

    const is_mobile = this.getIsMobile();

    /**
     * @desc 크리에이터 샵 헤더는 모바일에서 무조건 뒤로가기 버튼이 존재
     */
    this.state.has_back_btn = is_mobile;

    const plus_creator_logo_props: CreatorLogoProps = {
      plus: data?.plus,
      creator: data.creator,
      is_mobile,
      theme: this.state.transparent ? 'dark' : 'bright',
    };

    this.#logo = new CreatorLogo(plus_creator_logo_props);
    this.#search_input_logo = new CreatorLogoSearch({ profile_url: data.creator.profile_url });
    this.#search_module_logo = new CreatorLogoSearch({ profile_url: data.creator.profile_url });

    this.#input_search = new InputSearch({
      ...data.search,
      transparent: option.transparent,
      backdrop_filter: option.transparent,
      off_reset: true,
      klass: this.INPUT_SEARCH_KLASS,
      left_part: this.#search_input_logo,
    });

    if (is_mobile && this.state.sub_header?.use !== false) {
      this.#sub_header = new CreatorShopSubHeader({
        menus: this.data.menus,
        plus_domain: data?.plus?.domain_name,
      });
    }

    if (is_mobile && this.data.category_menus?.length) {
      this.#category_sub_header = new CreatorShopSubHeaderCategory({
        menus: this.data.category_menus,
        plus_domain: data?.plus?.domain_name,
        store_domain: data.creator.domain_name,
      });
    }

    if (!data.right?.off_search) {
      this.#search_module = is_mobile
        ? new SearchModuleCreatorShop(this.data.search ?? {}, {
            klass: klass.search_module,
            is_mobile,
            highlight: 'stroke',
            without_visible_interaction: true,
            search_input_left_part: this.#search_module_logo,
            search_type: 'CREATOR_SHOP',
            store_id: this.data.creator.id,
            store_domain_name: this.data.creator.domain_name,
          })
        : new SearchModuleCreatorShop(data.search ?? {}, {
            klass: klass.search_module,
            is_mobile,
            highlight: 'fill',
            search_input_left_part: this.#search_module_logo,
            search_type: 'CREATOR_SHOP',
            store_id: this.data.creator.id,
            store_domain_name: this.data.creator.domain_name,
          });
    }
  }

  protected override template() {
    const { transparent, is_fixed } = this.state;

    const is_mobile = this.getIsMobile();

    const container_classes = classes(klass.header, {
      [klass.transparent]: transparent,
      [klass.fixed]: is_fixed,
    });

    const left_logo = html`<a
      href="${makeMainUrl(`/${ET.lang}`)}"
      class="${klass.left_logo_wrapper} ${klass.creator_shop}"
      >${MarppleShopLogo}</a
    >`;

    return html`<div class="${klass.header_outer_container}">
      <div class="${container_classes}">
        <div class="${klass.core}">
          ${is_mobile ? '' : left_logo}
          <div class="${klass.content}">${is_mobile ? this.#mobileTemplate() : this.#pcTemplate()}</div>
        </div>
        ${this.#sub_header ? this.#sub_header : ''}
        ${this.#category_sub_header ? this.#category_sub_header : ''}
      </div>
      ${this.#search_module && is_mobile
        ? new Modal({
            size: 'large',
            body: this.#search_module,
            isOpen: false,
            full_width: true,
          })
        : ''}
    </div>`;
  }

  #pcTemplate() {
    const { menus, cart_count, right, plus } = this.data;

    return html`<div class="${klass.left_part}">
        ${this.#logo}
        <div class="${klass.menu_container}">
          ${menus.map(
            (menu) =>
              html`<a
                href="${makePlusUrl(plus?.domain_name, menu.url)}"
                target="${menu.target || '_self'}"
                class="${klass.menu} ${htmlIf(klass.current, !!menu.is_selected)} ${font('16_bold')}"
              >
                ${menu.name}
              </a>`,
          )}
        </div>
      </div>

      <div class="${klass.right_part}">
        <div class="${klass.search_bar_container}">${this.#input_search}</div>
        <div class="${klass.icons}">
          ${right?.off_search ? '' : html`<span class="${klass.search_icon}">${SearchIcon}</span>`}
          <a href="/${ET.lang}/@/my_like" class="${klass.icon}">${HeartFillIcon}</a>
          <a href="/${ET.lang}/@/cart" class="${klass.icon}">${CartFillIcon({ count: cart_count })}</a>
          <a href="/${ET.lang}/@/mypage" class="${klass.icon}">${UserFillIcon}</a>
        </div>
        ${this.#search_module ?? ''}
      </div>`;
  }

  #mobileTemplate() {
    const { has_back_btn, has_close_btn } = this.state;
    const { cart_count, left, right } = this.data;

    const left_part =
      left?.whole ??
      html`<a
          href="${makeMainUrl(`/${ET.lang}`)}"
          data-post-message="${dataStr(makeHomeScreenNavigate())}"
          class="${klass.left_logo_wrapper}"
          >${left?.logo ?? MarppleShopLogo}</a
        >
        ${this.#logo}`;

    const back_btn_html = htmlIf(
      html`<span class="${klass.back_arrow_btn}">${ArrowLeftIcon}</span>`,
      has_back_btn,
    );

    const center_part = this.data.center?.whole ?? '';

    const right_part =
      right?.whole ??
      html` <div class="${klass.icons}">
        ${right?.off_search ? '' : html`<span class="${klass.search_icon}">${SearchIcon}</span>`}
        ${right?.off_cart
          ? ''
          : html`<a
              class="${klass.icon}"
              data-post-message="${dataStr(makeCartNavigate())}"
              href="/${ET.lang}/@/cart"
              >${CartFillIcon({ count: cart_count })}</a
            >`}
      </div>`;

    const close_btn_html = htmlIf(
      html`<span class="${klass.close_btn}">${CloseIcon()}</span>`,
      has_close_btn,
    );

    return html` <div class="${klass.left_part} ${htmlIf(klass.full, !!left?.full_width)}">
        ${back_btn_html} ${left_part}
        <div class="${klass.center_part} ${font('16_bold')}">${center_part}</div>
      </div>
      <div class="${klass.right_part}">${right_part} ${close_btn_html}</div>`;
  }

  override onRender() {
    const is_mobile = this.getIsMobile();

    // 검색 헤더
    if (is_mobile) {
      const modalView = this.subView(Modal);
      this.delegate('click', `.${klass.search_icon}`, () => {
        if (modalView) {
          modalView.open();
        }
      });
      this.delegate(SearchModuleCreatorShopCloseEvent, SearchModuleCreatorShop, () => {
        if (modalView) {
          modalView.close();
        }
      });
    } else {
      const SearchModuleCreatorShopView = this.subView(SearchModuleCreatorShop);
      const openSearchModuleCreatorShopView = () => {
        if (SearchModuleCreatorShopView) {
          SearchModuleCreatorShopView.open();
        }
      };
      const closeSearchModuleCreatorShopView = () => {
        if (SearchModuleCreatorShopView) {
          const value = SearchModuleCreatorShopView.close();
          const input_search_view = this.subView(InputSearch);
          input_search_view?.setValue(value);
        }
      };
      listenDocumentEvent('header_unpinned', closeSearchModuleCreatorShopView);
      this.delegate('focusin', InputSearch, (e) => {
        // 인풋에 있는 로고 클릭 시 검색 모듈 안열리게 추가
        if (e.target === this.#search_input_logo.element()) {
          return;
        }
        openSearchModuleCreatorShopView();
      });
      this.delegate('click', `.${klass.search_icon}`, openSearchModuleCreatorShopView);
      document.addEventListener('click', (e) => {
        if (!this.element().contains(e.target as HTMLElement)) {
          closeSearchModuleCreatorShopView();
        }
      });
    }

    // 크리에이터 샵 검색을 끄는 경우 처리
    this.delegate(CreatorSearchStatusChange, CreatorLogoSearch, (e) => {
      this.#search_module?.changeSearchType(e.detail.status === 'enable' ? 'CREATOR_SHOP' : 'ALL');
      this.#search_input_logo.changeStatus(e.detail.status);
      this.#search_module_logo.changeStatus(e.detail.status);
    });

    this.init();
  }

  transparentEnable() {
    if (this.#input_search.isRendered()) {
      this.#input_search.element().classList.add(InputSearch.TRANSPARENT_KLASS);
    }
    if (this.#search_input_logo.isRendered()) {
      this.#search_input_logo.toggleTransparent(true);
    }
  }

  transparentDisable() {
    if (this.#input_search.isRendered()) {
      this.#input_search.element().classList.remove(InputSearch.TRANSPARENT_KLASS);
    }
    if (this.#search_input_logo.isRendered()) {
      this.#search_input_logo.toggleTransparent(false);
    }
  }

  override onTransparent(instant: boolean) {
    this.#logo.changeTheme(this.state.transparent ? 'dark' : 'bright', instant);
  }

  static getLogoData(
    { store, plus_crew }: RequiredByKey<Locals, 'store' | 'plus_crew'>,
    option?: { no_navigate?: boolean },
  ): {
    plus: HeaderCreatorShopData['plus'];
    creator: HeaderCreatorShopData['creator'];
  } {
    return {
      plus: isAllCrewById(plus_crew.id)
        ? undefined
        : {
            domain_name: plus_crew.domain_name,
            name: plus_crew.brand_title || plus_crew.name,
            logo_url: plus_crew.pc_main_logo,
            link: `/${ET.lang}`,
          },
      creator: {
        id: store.id,
        domain_name: store.domain_name,
        logo_url: store.logo_img,
        profile_url: store.thumbnail_img,
        link: `/${ET.lang}/${store.domain_name}`,
        navigate: option?.no_navigate
          ? undefined
          : makeEmptyScreenNavigate(
              isAllCrewById(plus_crew.id)
                ? `/${ET.lang}/${store.domain_name}`
                : makePlusUrl(plus_crew.domain_name, `/${ET.lang}/${store.domain_name}`),
            ),
        name: store.name,
      },
    };
  }

  static getSearchModuleCreatorShopData(value?: string): HeaderCreatorShopData['search'] {
    return {
      placeholder: getSearchInputPlaceholder(ET.lang),
      value,
    };
  }

  static processSelectMenu(menus: Menu[], select_key?: string, sub_select_key?: string): Menu[] {
    return pipe(
      menus,
      map((menu) => {
        return {
          ...menu,
          // 메뉴가 1개라면 무조건 활성화
          is_selected: menu.key === select_key || menus.length < 2,
          sub_menus: menu.sub_menus
            ? HeaderCreatorShop.processSelectMenu(menu.sub_menus, sub_select_key)
            : undefined,
        };
      }),
      toArray,
    );
  }
}
